import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Clock, CheckCircle, XCircle } from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { StatusBadge, PriorityBadge } from "../shared/Badge";
import { UserAvatar } from "../shared/UserAvatar";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { formatRelativeTime, getTicketTypeConfig } from "../../lib/utils";
import { toast } from "sonner";

export function Approvals() {
    const { currentUser, getUserById } = useApp();
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (currentUser) {
            loadApprovals();
        }
    }, [currentUser]);

    const loadApprovals = async () => {
        try {
            setLoading(true);
            const data = await api.getPendingApprovals(currentUser.id);
            setApprovals(data);
        } catch (error) {
            console.error("Failed to load approvals:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDecision = async (approvalId, status) => {
        try {
            await api.decideApproval(approvalId, status, null, currentUser.id);
            toast.success(status === "approved" ? "Approuvé" : "Rejeté");
            loadApprovals();
        } catch (error) {
            toast.error("Erreur lors de la décision");
        }
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="page-container" data-testid="approvals-page">
            <div className="page-header">
                <h1 className="page-title">Approbations</h1>
                <p className="page-subtitle">
                    {approvals.length} demande{approvals.length !== 1 ? "s" : ""} en attente
                </p>
            </div>

            {approvals.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={CheckSquare}
                        title="Aucune approbation en attente"
                        description="Vous n'avez aucune demande à approuver pour le moment"
                    />
                </div>
            ) : (
                <div className="space-y-4">
                    {approvals.map((approval) => {
                        const ticket = approval.ticket;
                        const requester = getUserById(ticket?.requester_id);
                        const typeConfig = getTicketTypeConfig(ticket?.type);
                        const isOnBehalf = approval.on_behalf_of === true;
                        const approver = isOnBehalf ? getUserById(approval.approver_id) : null;

                        return (
                            <div
                                key={approval.id}
                                className={`card ${isOnBehalf ? "border-l-4 border-l-indigo-400" : ""}`}
                                data-testid={`approval-card-${approval.id}`}
                            >
                                <div className="card-body">
                                    <div className="flex items-start gap-4">
                                        <UserAvatar user={requester} size="lg" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-mono text-gray-400">
                                                    {ticket?.ticket_number}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {typeConfig.label}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    Étape {approval.step_order}
                                                </span>
                                                {isOnBehalf && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700" data-testid={`behalf-badge-${approval.id}`}>
                                                        Au nom de {approver?.first_name} {approver?.last_name}
                                                    </span>
                                                )}
                                            </div>
                                            <Link
                                                to={`/tickets/${ticket?.id}`}
                                                className="text-base font-semibold text-gray-900 hover:text-blue-600"
                                            >
                                                {ticket?.title}
                                            </Link>
                                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                {ticket?.description}
                                            </p>
                                            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                                                <span>
                                                    Demandé par {requester?.first_name}{" "}
                                                    {requester?.last_name}
                                                </span>
                                                <span>
                                                    {formatRelativeTime(ticket?.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-3">
                                            <div className="flex items-center gap-2">
                                                <StatusBadge status={ticket?.status} />
                                                <PriorityBadge priority={ticket?.priority} />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleDecision(approval.id, "rejected")
                                                    }
                                                    data-testid={`reject-${approval.id}`}
                                                >
                                                    <XCircle className="w-4 h-4 mr-1" />
                                                    Rejeter
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() =>
                                                        handleDecision(approval.id, "approved")
                                                    }
                                                    data-testid={`approve-${approval.id}`}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                    Approuver
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
